import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Copy, Check, Download, ArrowLeft, Mail, Lock, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LOGO_WHITE_URL } from "../data";
import { buildSignatureHTML } from "../emailSignature";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rcp_admin_token";

const ASSETS = [
  { name: "Full Logo (Rock Chicks · Productions)", file: "/brand/rock-chicks-logo-full.png", tag: "Logo", preview: "/brand/rock-chicks-logo-full.png", dark: true },
  { name: "Logo — White (transparent PNG)", file: "/brand/rock-chicks-logo-white.png", tag: "Logo", preview: "/brand/rock-chicks-logo-white.png", dark: true },
  { name: "Logo — Black (PNG)", file: "/brand/rock-chicks-logo-black.png", tag: "Logo", preview: "/brand/rock-chicks-logo-black.png", dark: false },
  { name: "Facebook Cover (1640×624)", file: "/brand/rock-chicks-facebook-cover.png", tag: "Social", preview: "/brand/rock-chicks-facebook-cover.png", dark: true },
  { name: "Profile Image (1024×1024)", file: "/brand/rock-chicks-profile.png", tag: "Social", preview: "/brand/rock-chicks-profile.png", dark: true },
  { name: "Favicon / App Icon (512×512)", file: "/brand/favicon-512.png", tag: "Icon", preview: "/brand/favicon-512.png", dark: true },
];

const downloadAsset = async (path, filename) => {
  try {
    const res = await fetch(path);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloading ${filename}`);
  } catch {
    window.open(path, "_blank");
  }
};

function AdminContent({ onLogout }) {
  const [copied, setCopied] = useState(false);
  const signatureHTML = buildSignatureHTML();

  const copyHTML = async () => {
    try {
      await navigator.clipboard.writeText(signatureHTML);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = signatureHTML;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      ta.remove();
      if (!ok) {
        toast.error("Copy failed — select the code and copy manually.");
        return;
      }
    }
    setCopied(true);
    toast.success("Signature HTML copied to clipboard");
    setTimeout(() => setCopied(false), 2200);
  };

  const copyRendered = async () => {
    try {
      const blob = new Blob([signatureHTML], { type: "text/html" });
      const data = [new window.ClipboardItem({ "text/html": blob })];
      await navigator.clipboard.write(data);
      toast.success("Formatted signature copied — paste into Gmail/Outlook");
    } catch {
      toast.error("Rich copy not supported here — use 'Copy HTML' instead.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grain">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_WHITE_URL} alt="Rock Chicks Productions" className="h-8 w-auto" />
            <span className="font-display font-semibold uppercase tracking-[0.3em] text-[10px] text-white/60 border-l border-white/20 pl-3">
              Brand Kit
            </span>
          </div>
          <div className="flex items-center gap-6">
          <Link
            data-testid="admin-back-link"
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white transition-colors"
          >
            <LogOut size={16} /> Lock
          </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E879F9] font-bold mb-3">Admin · Toolkit</p>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-[0.9]">
            Brand <span className="text-[#b533ff] text-glow">assets.</span>
          </h1>
          <p className="mt-5 text-gray-400 font-light max-w-xl">
            Your email signature and downloadable brand imagery — ready to grab whenever you need them.
          </p>
        </motion.div>

        {/* Email signature */}
        <section className="mt-16" data-testid="admin-signature-section">
          <div className="flex items-center gap-3 mb-6">
            <Mail size={18} className="text-[#b533ff]" />
            <h2 className="font-display font-bold uppercase text-xl">Email Signature</h2>
          </div>

          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {/* Rendered preview on white (as it appears in email) */}
            <div className="bg-white p-8">
              <div data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: signatureHTML }} />
            </div>
            {/* Actions + code */}
            <div className="bg-[#0a0a0c] p-6 border-t border-white/10">
              <div className="flex flex-wrap gap-3 mb-5">
                <button
                  data-testid="copy-signature-html"
                  onClick={copyHTML}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#b533ff] text-white text-sm font-semibold uppercase tracking-[0.12em] hover:shadow-[0_0_24px_rgba(181,51,255,0.6)] transition-shadow"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? "Copied" : "Copy HTML"}
                </button>
                <button
                  data-testid="copy-signature-rendered"
                  onClick={copyRendered}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm font-semibold uppercase tracking-[0.12em] hover:border-[#b533ff] hover:bg-[#b533ff]/10 transition-colors"
                >
                  <Copy size={16} /> Copy formatted
                </button>
              </div>
              <pre className="text-xs text-gray-400 bg-black/50 border border-white/10 rounded-xl p-4 overflow-x-auto max-h-56">
                <code>{signatureHTML}</code>
              </pre>
              <p className="mt-3 text-xs text-gray-500">
                Gmail: Settings → See all settings → Signature → paste. Outlook: File → Options → Mail → Signatures.
                Use “Copy formatted” to paste it styled, or “Copy HTML” for the raw source.
              </p>
            </div>
          </div>
        </section>

        {/* Downloads */}
        <section className="mt-20" data-testid="admin-downloads-section">
          <div className="flex items-center gap-3 mb-6">
            <Download size={18} className="text-[#b533ff]" />
            <h2 className="font-display font-bold uppercase text-xl">Downloads</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ASSETS.map((a) => (
              <div
                key={a.file}
                data-testid={`asset-${a.tag}-${a.file.split("/").pop()}`}
                className="group rounded-2xl border border-white/10 bg-[#0a0a0c] overflow-hidden hover:border-[#b533ff]/50 transition-colors"
              >
                <div
                  className={`h-44 flex items-center justify-center p-6 ${a.dark ? "bg-[#050505]" : "bg-white"}`}
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 50% 50%, rgba(181,51,255,0.14), transparent 70%)",
                  }}
                >
                  <img src={a.preview} alt={a.name} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="p-5 flex items-center justify-between gap-3">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#E879F9] font-bold">{a.tag}</span>
                    <p className="text-sm text-white mt-1 leading-snug">{a.name}</p>
                  </div>
                  <button
                    data-testid={`download-${a.file.split("/").pop()}`}
                    onClick={() => downloadAsset(a.file, a.file.split("/").pop())}
                    aria-label={`Download ${a.name}`}
                    className="shrink-0 w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-[#b533ff] hover:bg-[#b533ff]/10 transition-colors"
                  >
                    <Download size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}


function AdminGate() {
  const [status, setStatus] = useState("checking"); // checking | locked | authed
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus("locked");
      return;
    }
    axios
      .get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setStatus("authed"))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setStatus("locked");
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setStatus("authed");
      setPassword("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setStatus("locked");
    toast.success("Locked");
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#b533ff]" size={28} />
      </div>
    );
  }

  if (status === "authed") return <AdminContent onLogout={logout} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white grain flex items-center justify-center px-5">
      <div className="absolute left-1/2 -translate-x-1/2 top-1/4 w-[500px] h-[500px] rounded-full bg-[#b533ff]/15 blur-[150px]" />
      <motion.form
        data-testid="admin-gate-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-sm bg-[#0a0a0c] border border-white/10 rounded-2xl p-8 sm:p-10"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO_WHITE_URL} alt="Rock Chicks Productions" className="h-12 w-auto mb-6" />
          <span className="w-12 h-12 rounded-full border border-[#b533ff]/40 flex items-center justify-center text-[#b533ff] mb-4">
            <Lock size={20} />
          </span>
          <h1 className="font-display font-bold uppercase text-xl tracking-tight">Brand Kit</h1>
          <p className="text-sm text-gray-500 mt-2">Enter the password to continue.</p>
        </div>

        <input
          data-testid="admin-password-input"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-transparent border-b border-white/20 py-3 text-center text-white placeholder:text-gray-600 focus:border-[#b533ff] focus:outline-none transition-colors"
        />
        {error && (
          <p data-testid="admin-error" className="text-sm text-red-400 mt-4 text-center">
            {error}
          </p>
        )}
        <button
          data-testid="admin-unlock"
          type="submit"
          disabled={submitting}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#b533ff] text-white font-semibold uppercase tracking-[0.15em] text-sm hover:shadow-[0_0_30px_rgba(181,51,255,0.7)] transition-shadow disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
          {submitting ? "Checking" : "Unlock"}
        </button>
        <Link
          to="/"
          className="mt-6 block text-center text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-white transition-colors"
        >
          Back to site
        </Link>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  return <AdminGate />;
}
